import React, { useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormHelperText, Grid, IconButton,
  MenuItem, Radio, RadioGroup, Select, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Close, Delete, ExpandMore, InfoOutlined, OpenInNew } from "@mui/icons-material";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import ColorPicker from "../color/ColorPicker";
import { parseColorJSON, rgbaToHex } from "../color/color";
import { LIMITES, TIPO, VENTA_MESA, nombreTipo, preguntaVacia, tarifaVacia } from "../../domain/tarifa/schema";
import { aInput, duplicarTarifa, fromTarifa, toPayload } from "../../domain/tarifa/payload";
import { hayErrores, validarTarifa } from "../../domain/tarifa/validacion";
import ConfigMesaTarifa from "./ConfigMesaTarifa";

const PRIMARIO = "#5656ff";
const TITULO = "#0b0062";
const FUENTE = "Bricolage Grotesque";

const bordesSx = {
  "& fieldset": { borderColor: "#B2BAC2" },
  "&:hover fieldset": { borderColor: PRIMARIO },
  "&.Mui-focused fieldset": { borderColor: PRIMARIO },
};
const campoSx = {
  mb: 2,
  "& label.Mui-focused": { color: PRIMARIO, fontFamily: FUENTE },
  "& .MuiOutlinedInput-root": { height: "40px", ...bordesSx },
};
const campoMultilineaSx = { ...campoSx, "& .MuiOutlinedInput-root": bordesSx };
const controlSx = {
  "& .MuiOutlinedInput-root": { fontFamily: FUENTE, height: "40px", ...bordesSx },
  "& .MuiFormHelperText-root": { fontFamily: FUENTE },
};
const selectSx = {
  backgroundColor: "#fff",
  "& .MuiSelect-select": { padding: "8px 14px", display: "flex", alignItems: "center", height: "24px" },
};

const SwitchChico = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": { opacity: 1, backgroundColor: PRIMARIO },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], { duration: 200 }),
  },
  "& .MuiSwitch-track": { borderRadius: 8, opacity: 1, backgroundColor: "rgba(0,0,0,.25)", boxSizing: "border-box" },
}));

const Etiqueta = ({ children, obligatorio = false }) => (
  <Typography variant="body1" sx={{ mb: 1, display: "block", color: TITULO }}>
    {children} {obligatorio && <span style={{ color: "red" }}>*</span>}
  </Typography>
);

const Opcion = ({ checked, onChange, texto, ayuda }) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ "&.Mui-checked": { color: PRIMARIO } }} />}
    label={
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {texto}
        <Tooltip title={ayuda} arrow placement="top">
          <InfoOutlined fontSize="small" sx={{ ml: 1, color: PRIMARIO, cursor: "help" }} />
        </Tooltip>
      </Box>
    }
  />
);

const colorHex = (color) => {
  try {
    return rgbaToHex(parseColorJSON(color));
  } catch {
    return PRIMARIO;
  }
};

// tarifas que pueden ir antes de esta: no la propia, ni una que ya tiene
// siguiente, ni una que viene después de esta en la cadena
const posiblesPrevias = (tarifas, form) => {
  const tomadas = new Set(tarifas.filter((t) => t.id !== form.id && t.idtarifa_previa).map((t) => t.idtarifa_previa));
  const anterior = new Map(tarifas.map((t) => [t.id, t.idtarifa_previa]));
  const vieneDespues = (id) => {
    for (let actual = id, pasos = 0; actual && pasos < tarifas.length; actual = anterior.get(actual), pasos++) {
      if (actual === form.id) return true;
    }
    return false;
  };
  return tarifas.filter((t) => t.id !== form.id && !t.privado && !tomadas.has(t.id) && !(form.id && vieneDespues(t.id)));
};

// cómo se vende cada ubicación, para elegirla sin tener que abrir el mapa
const descripcionUbicacion = (u) => {
  if (u.mesas?.length) return `${u.mesas.length} ${u.mesas.length === 1 ? "mesa" : "mesas"}`;
  if (u.asientos?.length) return `${u.asientos.length} asientos numerados`;
  return `General · ${u.limite_maximo} lugares`;
};

const formInicial = ({ tarifa, duplicar, idtipo, ubicaciones }) => {
  if (!tarifa) return tarifaVacia(idtipo, { hayUbicaciones: ubicaciones.length > 0 });
  return duplicar ? duplicarTarifa(tarifa) : fromTarifa(tarifa);
};

/* tarifa: la que se edita (o se duplica, con duplicar); null para crear una del tipo idtipo.
   tarifasTickets: los tickets del evento, para elegir la tanda anterior */
const FormularioTarifa = ({
  eventoId, idtipo, tarifa = null, duplicar = false, fechasEvento = [], ubicaciones = [], tarifasTickets = [],
  esPlanGratuito = false, onCerrar, onGuardada,
}) => {
  const [form, setForm] = useState(() => formInicial({ tarifa, duplicar, idtipo, ubicaciones }));
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [colorAbierto, setColorAbierto] = useState(false);

  const esTicket = form.idtipo === TIPO.TICKET;
  const esNueva = !form.id;
  const finEvento = useMemo(
    () => (fechasEvento.length ? moment.max(fechasEvento.map((f) => moment(f.hora_fin))) : null),
    [fechasEvento]
  );
  const ubicacionElegida = ubicaciones.find((u) => u.id === Number(form.idubicacion));
  const previas = useMemo(() => posiblesPrevias(tarifasTickets, form), [tarifasTickets, form]);
  const enTandas = esTicket && !form.privado && form.por_tandas;
  // la venta de mesa entera sólo existe si la ubicación elegida tiene mesas
  const conMesas = esTicket && !form.nueva_ubicacion && (ubicacionElegida?.mesas?.length ?? 0) > 0;

  const set = (campo, valor) => {
    setForm((f) => ({ ...f, [campo]: valor }));
    if (errores[campo]) {
      setErrores(({ [campo]: _, ...resto }) => resto);
    }
  };

  const setPregunta = (indice, cambios) =>
    set("preguntas", form.preguntas.map((p, i) => (i === indice ? { ...p, ...cambios } : p)));

  const guardar = async (e) => {
    e.preventDefault();
    const aGuardar = conMesas ? form : { ...form, venta_mesa: VENTA_MESA.SILLA };
    const encontrados = validarTarifa(aGuardar, { finEvento, tarifas: tarifasTickets });
    setErrores(encontrados);
    if (hayErrores(encontrados)) {
      toast.error("Revisá los campos marcados.");
      return;
    }

    setGuardando(true);
    try {
      const payload = toPayload(aGuardar, { eventoId });
      const respuesta = esNueva
        ? await api.tarifas.createTarifa(payload)
        : await api.tarifas.updateTarifa(form.id, payload);
      if (respuesta.status !== "success") throw new Error(respuesta.message || "No se pudo guardar la tarifa.");

      toast.success(esNueva ? "Tarifa creada." : "Tarifa actualizada.");
      onGuardada(respuesta.data.tarifa);
    } catch (error) {
      toast.error(error.message || "No se pudo conectar con el servidor.");
    } finally {
      setGuardando(false);
    }
  };

  const textoAccion = `${esNueva ? "Crear" : "Guardar"} ${nombreTipo(form.idtipo).toLowerCase()}`;

  return (
    <Dialog
      open
      onClose={(_, motivo) => motivo !== "backdropClick" && !guardando && onCerrar()}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontFamily: FUENTE, color: TITULO }}>
            {duplicar ? "Duplicar tarifa" : esNueva ? "Crear tarifa" : "Editar tarifa"} - {nombreTipo(form.idtipo)}
          </Typography>
          <IconButton onClick={onCerrar} disabled={guardando}><Close /></IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form id="formularioTarifa" onSubmit={guardar} noValidate>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense" sx={controlSx}>
                <Etiqueta obligatorio>Visibilidad</Etiqueta>
                <Select sx={selectSx} value={String(form.privado)} onChange={(e) => set("privado", e.target.value === "true")}>
                  <MenuItem value="false">Pública</MenuItem>
                  <MenuItem value="true">Privada</MenuItem>
                </Select>
                <FormHelperText>
                  {form.privado
                    ? "No se muestra al público ni se vende online: sólo en el punto de venta o como cortesía."
                    : "Se muestra en la página del evento y se vende online durante el período de venta."}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Etiqueta obligatorio>Nombre de la tarifa</Etiqueta>
              <TextField
                sx={campoSx}
                fullWidth
                margin="dense"
                value={form.concepto}
                onChange={(e) => set("concepto", e.target.value)}
                inputProps={{ maxLength: LIMITES.CONCEPTO_MAX }}
                error={!!errores.concepto}
                helperText={errores.concepto}
              />
            </Grid>

            <Grid item xs={12}>
              <Etiqueta>Descripción</Etiqueta>
              <TextField
                sx={campoMultilineaSx}
                fullWidth
                multiline
                rows={2}
                margin="dense"
                value={form.descripcion}
                onChange={(e) => set("descripcion", e.target.value)}
                inputProps={{ maxLength: LIMITES.DESCRIPCION_MAX }}
                error={!!errores.descripcion}
                helperText={errores.descripcion}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Etiqueta obligatorio>Precio</Etiqueta>
              <TextField
                sx={campoSx}
                fullWidth
                margin="dense"
                type="number"
                value={form.precio}
                onChange={(e) => set("precio", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                error={!!errores.precio}
                helperText={errores.precio}
              />
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              {Number(form.precio) > 0 && (
                <Opcion
                  checked={form.asumir_comision}
                  onChange={(v) => set("asumir_comision", v)}
                  texto="Asumir costos de servicio"
                  ayuda="Vos pagás los cargos por servicio y el comprador ve exactamente el precio que pusiste."
                />
              )}
              {esPlanGratuito && (
                <Typography variant="caption" sx={{ color: "#FF9800", mb: 1 }}>
                  Con el plan gratuito se aplica una comisión del 15%.
                </Typography>
              )}
            </Grid>

            {esTicket && !form.privado && (
              <Grid item xs={12}>
                <FormControlLabel
                  sx={{ ml: 1, mb: 1 }}
                  control={
                    <SwitchChico
                      sx={{ mr: 2 }}
                      checked={form.por_tandas}
                      onChange={(e) => {
                        set("por_tandas", e.target.checked);
                        if (!e.target.checked) set("idtarifa_previa", "");
                      }}
                    />
                  }
                  label="Vender por tandas"
                />
                {form.por_tandas && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                    <Typography sx={{ fontSize: 14, color: "#7B7C88", whiteSpace: "nowrap" }}>
                      Se habilita al agotarse
                    </Typography>
                    <FormControl sx={{ ...controlSx, flex: 1, minWidth: 200 }} error={!!errores.idtarifa_previa}>
                      <Select
                        sx={selectSx}
                        displayEmpty
                        value={form.idtarifa_previa}
                        onChange={(e) => {
                          set("idtarifa_previa", e.target.value);
                          // la tanda arranca con la anterior: su propio inicio no se carga
                          const previa = tarifasTickets.find((t) => t.id === e.target.value);
                          if (previa) set("inicio_venta", aInput(previa.inicio_venta));
                        }}
                      >
                        <MenuItem value="" disabled>Elegí una tarifa</MenuItem>
                        {previas.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.concepto}</MenuItem>
                        ))}
                      </Select>
                      {errores.idtarifa_previa && <FormHelperText>{errores.idtarifa_previa}</FormHelperText>}
                    </FormControl>
                  </Box>
                )}
              </Grid>
            )}

            {!form.privado && (
              <>
                <Grid item xs={12}>
                  <Etiqueta obligatorio>{enTandas ? "¿Hasta cuándo se vende?" : "¿Cuándo empieza y termina la venta?"}</Etiqueta>
                </Grid>
                {!enTandas && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      sx={campoSx}
                      label="Inicio de la venta"
                      type="datetime-local"
                      value={form.inicio_venta}
                      onChange={(e) => set("inicio_venta", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      margin="dense"
                      error={!!errores.venta}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    sx={campoSx}
                    label="Fin de la venta"
                    type="datetime-local"
                    value={form.fin_venta}
                    onChange={(e) => set("fin_venta", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: finEvento?.format("YYYY-MM-DDTHH:mm") }}
                    fullWidth
                    margin="dense"
                    error={!!errores.venta}
                  />
                </Grid>
                {errores.venta && (
                  <Grid item xs={12}>
                    <FormHelperText error sx={{ mt: -1, mb: 1 }}>{errores.venta}</FormHelperText>
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <Etiqueta obligatorio>¿A qué fechas del evento corresponde?</Etiqueta>
              <FormControl fullWidth margin="dense" sx={controlSx} error={!!errores.fechas}>
                <Select
                  sx={selectSx}
                  multiple
                  value={form.fechas}
                  onChange={(e) => set("fechas", [...new Set(e.target.value)])}
                  renderValue={(elegidas) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {elegidas.map((id) => {
                        const fecha = fechasEvento.find((f) => f.id === id);
                        return <Chip key={id} size="small" label={fecha ? moment(fecha.hora_inicio).format("D [de] MMM, HH:mm") : id} />;
                      })}
                    </Box>
                  )}
                >
                  {fechasEvento.map((fecha) => (
                    <MenuItem key={fecha.id} value={fecha.id}>
                      <Checkbox checked={form.fechas.includes(fecha.id)} />
                      {moment(fecha.hora_inicio).format("D [de] MMMM YYYY, HH:mm")}
                      {fecha.oculta && <Typography component="span" sx={{ ml: 1, color: "#7B7C88" }}>(oculta)</Typography>}
                    </MenuItem>
                  ))}
                </Select>
                {errores.fechas && <FormHelperText>{errores.fechas}</FormHelperText>}
              </FormControl>
            </Grid>

            {esTicket ? (
              <>
                <Grid item xs={12}>
                  <Etiqueta obligatorio>Ubicación</Etiqueta>
                  <Typography sx={{ fontSize: 12, color: "#7B7C88", mb: 1 }}>
                    Dónde se vende la entrada. La ubicación define el total disponible y cuántas puede comprar cada
                    persona; si tiene varias tarifas, lo comparten.
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={form.nueva_ubicacion ? "nueva" : "existente"}
                    onChange={(_, valor) => valor && set("nueva_ubicacion", valor === "nueva")}
                    sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 2 } }}
                  >
                    <ToggleButton value="existente" disabled={ubicaciones.length === 0}>Elegir una existente</ToggleButton>
                    <ToggleButton value="nueva">Crear una general</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {form.nueva_ubicacion ? (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        sx={campoSx}
                        label="Nombre de la ubicación"
                        fullWidth
                        margin="dense"
                        value={form.nombre_ubicacion}
                        onChange={(e) => set("nombre_ubicacion", e.target.value)}
                        error={!!errores.nombre_ubicacion}
                        helperText={errores.nombre_ubicacion || "Se vende por cantidad. Si querés que el comprador elija su lugar, después dibujala en el mapa con filas o mesas."}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        sx={campoSx}
                        label="Límite por comprador"
                        type="number"
                        fullWidth
                        margin="dense"
                        value={form.limite_persona}
                        onChange={(e) => set("limite_persona", e.target.value)}
                        error={!!errores.limite_persona}
                        helperText={errores.limite_persona}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        sx={campoSx}
                        label="Total de entradas"
                        type="number"
                        fullWidth
                        margin="dense"
                        value={form.limite_maximo}
                        onChange={(e) => set("limite_maximo", e.target.value)}
                        error={!!errores.limite_maximo}
                        helperText={errores.limite_maximo}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="dense" sx={controlSx} error={!!errores.idubicacion}>
                      <Select sx={selectSx} displayEmpty value={form.idubicacion} onChange={(e) => set("idubicacion", e.target.value)}>
                        <MenuItem value="" disabled>Elegí una ubicación</MenuItem>
                        {ubicaciones.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.ubicacion}
                            <Typography component="span" sx={{ ml: 1, fontSize: 13, color: "#7B7C88" }}>
                              {descripcionUbicacion(u)}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errores.idubicacion || (ubicaciones.length === 0 ? "El evento todavía no tiene ubicaciones." : "")}
                      </FormHelperText>
                    </FormControl>
                    {ubicacionElegida && (
                      <Box sx={{ mt: 1, mb: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                        <Typography variant="body2">
                          Límite por comprador: <strong>{ubicacionElegida.limite_persona}</strong> · Total:{" "}
                          <strong>{ubicacionElegida.limite_maximo}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7280" }}>
                          Los límites y el diseño se cambian en el mapa.{" "}
                          <a href={`/eventos/${eventoId}/mapa`} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARIO }}>
                            Abrir el mapa <OpenInNew sx={{ fontSize: 12, verticalAlign: "middle" }} />
                          </a>
                        </Typography>
                      </Box>
                    )}
                    {conMesas && (
                      <ConfigMesaTarifa
                        form={form}
                        set={set}
                        error={errores.mesa}
                        tarifasTickets={tarifasTickets}
                        ubicacion={ubicacionElegida}
                      />
                    )}
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Etiqueta obligatorio>Límites de venta</Etiqueta>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    sx={campoSx}
                    label="Límite por comprador"
                    type="number"
                    fullWidth
                    margin="dense"
                    value={form.limite_persona}
                    onChange={(e) => set("limite_persona", e.target.value)}
                    error={!!errores.limite_persona}
                    helperText={errores.limite_persona}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    sx={campoSx}
                    label="Total de vouchers"
                    type="number"
                    fullWidth
                    margin="dense"
                    value={form.limite_maximo}
                    onChange={(e) => set("limite_maximo", e.target.value)}
                    error={!!errores.limite_maximo}
                    helperText={errores.limite_maximo}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Accordion
                expanded={colorAbierto}
                onChange={() => setColorAbierto((v) => !v)}
                sx={{ boxShadow: "none", border: "1px solid #e0e0e0", borderRadius: "8px", "&:before": { display: "none" } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: PRIMARIO }} />}
                  sx={{ "& .MuiAccordionSummary-content": { display: "flex", alignItems: "center", gap: 2 } }}
                >
                  <Typography sx={{ fontWeight: 600, color: TITULO }}>Color de la tarifa</Typography>
                  {!colorAbierto && (
                    <Chip
                      label={colorHex(form.color).toUpperCase()}
                      sx={{ backgroundColor: colorHex(form.color), color: "#fff", fontWeight: 600, fontFamily: "monospace" }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <ColorPicker value={form.color} onChange={(color) => set("color", color)} />
                </AccordionDetails>
              </Accordion>
            </Grid>

            {esTicket && (
              <Grid item xs={12}>
                <Opcion
                  checked={form.informacion_adicional}
                  onChange={(v) => set("informacion_adicional", v)}
                  texto="Pedir información adicional"
                  ayuda="Preguntas que cada comprador responde al comprar (por ejemplo, DNI o teléfono)."
                />
                {form.informacion_adicional && (
                  <Box sx={{ mt: 1 }}>
                    {form.preguntas.map((pregunta, indice) => (
                      <Box key={indice} display="flex" alignItems="center" gap={1} mb={1}>
                        <TextField
                          label="Pregunta"
                          size="small"
                          fullWidth
                          value={pregunta.informacion_adicional}
                          onChange={(e) => setPregunta(indice, { informacion_adicional: e.target.value })}
                          inputProps={{ maxLength: LIMITES.PREGUNTA_MAX }}
                        />
                        <FormControlLabel
                          sx={{ whiteSpace: "nowrap", ml: 0.5 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={pregunta.obligatoria}
                              onChange={(e) => setPregunta(indice, { obligatoria: e.target.checked })}
                            />
                          }
                          label="Obligatoria"
                        />
                        {form.preguntas.length > 1 && (
                          <IconButton
                            aria-label="Quitar pregunta"
                            color="error"
                            onClick={() => set("preguntas", form.preguntas.filter((_, i) => i !== indice))}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    {errores.preguntas && <FormHelperText error>{errores.preguntas}</FormHelperText>}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        onClick={() => set("preguntas", [...form.preguntas, preguntaVacia()])}
                        disabled={
                          form.preguntas.length >= LIMITES.PREGUNTAS_MAX ||
                          !form.preguntas[form.preguntas.length - 1]?.informacion_adicional.trim()
                        }
                        sx={{ textTransform: "none", color: PRIMARIO }}
                      >
                        Agregar pregunta
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>
            )}

            {esNueva && !form.copiar_diseno_de && (
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Etiqueta obligatorio>Formato de impresión</Etiqueta>
                <RadioGroup row value={form.formato} onChange={(e) => set("formato", e.target.value)}>
                  <FormControlLabel value="tarjeta" control={<Radio sx={{ "&.Mui-checked": { color: PRIMARIO } }} />} label="Tarjeta" />
                  <FormControlLabel value="pulsera" control={<Radio sx={{ "&.Mui-checked": { color: PRIMARIO } }} />} label="Pulsera" />
                </RadioGroup>
              </Grid>
            )}

            {form.copiar_diseno_de && (
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  El diseño de impresión se copia de la tarifa original.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Opcion
                checked={form.preimpreso}
                onChange={(v) => set("preimpreso", v)}
                texto="Tickets preimpresos"
                ayuda="Usa tickets físicos preimpresos: habilita el pool de preimpresos en la venta online por asiento y en las cajas preimpresas."
              />
            </Grid>
            <Grid item xs={12}>
              <Opcion
                checked={form.consalidas}
                onChange={(v) => set("consalidas", v)}
                texto="Habilitar reingreso"
                ayuda="Los tickets de esta tarifa pueden entrar y salir del evento las veces que haga falta."
              />
            </Grid>
            {!(Number(form.precio) > 0) && (
              <Grid item xs={12}>
                <Opcion
                  checked={form.requiere_aprobacion}
                  onChange={(v) => set("requiere_aprobacion", v)}
                  texto="Requiere aprobación del organizador"
                  ayuda="Las solicitudes de esta tarifa gratuita quedan pendientes hasta que las apruebes o rechaces; recién ahí se envían los tickets."
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Opcion
                checked={form.imprimeqr}
                onChange={(v) => set("imprimeqr", v)}
                texto="Imprimir código QR"
                ayuda="Si el ticket o voucher impreso lleva el código QR."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                sx={{ ...campoSx, mt: 1 }}
                fullWidth
                type="number"
                label="Unidades de stock por venta"
                value={form.unidadstock}
                onChange={(e) => set("unidadstock", e.target.value)}
                inputProps={{ min: 1 }}
                error={!!errores.unidadstock}
                helperText={errores.unidadstock}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCerrar} disabled={guardando} sx={{ textTransform: "none", color: "#374151" }}>
          Cancelar
        </Button>
        <Button
          type="submit"
          form="formularioTarifa"
          variant="contained"
          disabled={guardando}
          sx={{ textTransform: "none", bgcolor: PRIMARIO, "&:hover": { bgcolor: "#4545dd" } }}
        >
          {guardando ? "Guardando..." : textoAccion}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormularioTarifa;
